import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';
import throttle from 'lodash/throttle';
import WidgetWrapper from './WidgetWrapper/WidgetWrapper';
import style from './GridLayout.module.css';

const CONTAINER_PADDING = [40, 20]; // [x, y]
const WIDGET_HEIGHT = 426;

function GridLayout({ widgets, onWidthChange, hidePadding, enableResponsiveHeight, widgetHeight, margin }) {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || !onWidthChange) return undefined;
    const notifyWidth = throttle(() => {
      if (containerRef.current) {
        onWidthChange(containerRef.current.clientWidth);
      }
    }, 150);

    notifyWidth();

    let resizeObserver;
    if (typeof window !== 'undefined' && 'ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(() => notifyWidth());
      resizeObserver.observe(containerRef.current);
    }

    window.addEventListener('resize', notifyWidth);

    return () => {
      window.removeEventListener('resize', notifyWidth);
      if (resizeObserver) resizeObserver.disconnect();
      notifyWidth.cancel && notifyWidth.cancel();
    };
  }, [onWidthChange]);

  const containerPadding = hidePadding ? [0, 0] : CONTAINER_PADDING; // [x, y]
  const columnGap = Array.isArray(margin) ? margin[0] : 10;
  const rowGap = Array.isArray(margin) ? margin[1] : 10;

  const sortedWidgets = Array.isArray(widgets)
    ? widgets.slice().sort((a, b) => {
        const ay = (a.coordinates && a.coordinates.y) || 0;
        const by = (b.coordinates && b.coordinates.y) || 0;
        if (ay !== by) return ay - by;
        const ax = (a.coordinates && a.coordinates.x) || 0;
        const bx = (b.coordinates && b.coordinates.x) || 0;
        return ax - bx;
      })
    : [];

  return (
    <div
      ref={containerRef}
      className={style.grid}
      style={{
        padding: `${containerPadding[1]}px ${containerPadding[0]}px`,
        columnGap,
        rowGap,
      }}
    >
      {sortedWidgets.map((w) => {
        const key = `${w.dashboardWidgetId || w.srlCardId}`;
        const itemHeight = enableResponsiveHeight ? widgetHeight : WIDGET_HEIGHT;
        return (
          <div key={key} style={{ height: `${itemHeight}px` }}>
            <WidgetWrapper>
              {w.widget}
            </WidgetWrapper>
          </div>
        );
      })}
    </div>
  );
}

GridLayout.propTypes = {
  widgets: PropTypes.array,
  onWidthChange: PropTypes.func,
  hidePadding: PropTypes.bool,
  enableResponsiveHeight: PropTypes.bool,
  widgetHeight: PropTypes.number,
  margin: PropTypes.array,
};

export default React.memo(GridLayout, isEqual);


